import React, { useMemo, useState, useEffect } from 'react';
import { apiClient } from '../../services/apiClient';
import { DataListTable, type Column } from '../../components/crm/DataListTable';

interface TierConfig {
  max_users: number;
  email_enabled: boolean;
  email_monthly_limit: number;
  custom_pipeline: boolean;
  automation_enabled: boolean;
  display_name: string;
}

interface EntitlementsConfig {
  tiers: Record<string, TierConfig>;
  tier_display_names: Record<string, string>;
}

interface FeatureSummaryRow {
  id: string;
  feature: string;
  values: Record<string, React.ReactNode>;
}

const EntitlementsManagement: React.FC = () => {
  const [config, setConfig] = useState<EntitlementsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.get('/admin/entitlements-config/');
      setConfig(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch entitlements config');
    } finally {
      setLoading(false);
    }
  };

  const featureRows = useMemo<FeatureSummaryRow[]>(() => {
    const tierEntries = Object.entries(config?.tiers || {});
    if (!config) return [];
    return [
      {
        id: 'max_users',
        feature: 'Max users',
        values: Object.fromEntries(
          tierEntries.map(([tierKey, tier]) => [
            tierKey,
            tier.max_users === -1 ? '∞' : tier.max_users,
          ])
        ),
      },
      {
        id: 'email_campaigns',
        feature: 'Email campaigns',
        values: Object.fromEntries(
          tierEntries.map(([tierKey, tier]) => [
            tierKey,
            tier.email_enabled
              ? tier.email_monthly_limit === -1
                ? '∞'
                : tier.email_monthly_limit.toLocaleString()
              : '-',
          ])
        ),
      },
      {
        id: 'custom_pipeline',
        feature: 'Custom pipeline',
        values: Object.fromEntries(
          tierEntries.map(([tierKey, tier]) => [tierKey, tier.custom_pipeline ? '✓' : '-'])
        ),
      },
      {
        id: 'automation',
        feature: 'Automation',
        values: Object.fromEntries(
          tierEntries.map(([tierKey, tier]) => [tierKey, tier.automation_enabled ? '✓' : '-'])
        ),
      },
    ];
  }, [config]);

  const featureColumns = useMemo<Column<FeatureSummaryRow>[]>(() => {
    const tierEntries = Object.entries(config?.tiers || {});
    return [
      {
        id: 'feature',
        header: 'Feature',
        mobilePriority: 'primary',
        mobileLabel: 'Feature',
        render: (row) => (
          <span className="text-sm font-medium text-gray-900">{row.feature}</span>
        ),
      },
      ...tierEntries.map(([tierKey, tier]) => ({
        id: tierKey,
        header: tier.display_name || tierKey,
        mobileLabel: tier.display_name || tierKey,
        className: 'text-center',
        cellClassName: 'text-center whitespace-normal',
        render: (row: FeatureSummaryRow) => (
          <span className="text-sm">{row.values[tierKey] ?? '-'}</span>
        ),
      })),
    ];
  }, [config]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading feature flags & entitlements...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
      </div>
    );
  }

  const tiers = Object.entries(config.tiers || {});

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900">Feature Flags & Entitlements</h2>
        <p className="text-sm text-gray-500 mt-1">
          Tier-based feature flags and limits. These drive backend enforcement.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tiers.map(([tierKey, tier]) => (
          <div
            key={tierKey}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="px-6 py-4 bg-indigo-50 border-b border-gray-200">
              <h3 className="text-lg font-bold text-indigo-900">
                {tier.display_name || tierKey.toUpperCase()}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Max users</span>
                <span className="font-medium">
                  {tier.max_users === -1 ? 'Unlimited' : tier.max_users}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Email enabled</span>
                <span className="font-medium">
                  {tier.email_enabled ? (
                    <span className="text-green-600">✓ Yes</span>
                  ) : (
                    <span className="text-gray-500">No</span>
                  )}
                </span>
              </div>
              {tier.email_enabled && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Email monthly limit</span>
                  <span className="font-medium">
                    {tier.email_monthly_limit === -1
                      ? 'Unlimited'
                      : tier.email_monthly_limit.toLocaleString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Custom pipeline</span>
                <span className="font-medium">
                  {tier.custom_pipeline ? (
                    <span className="text-green-600">✓ Yes</span>
                  ) : (
                    <span className="text-gray-500">No</span>
                  )}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Automation enabled</span>
                <span className="font-medium">
                  {tier.automation_enabled ? (
                    <span className="text-green-600">✓ Yes</span>
                  ) : (
                    <span className="text-gray-500">No</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Feature Summary</h3>
        <DataListTable<FeatureSummaryRow>
          columns={featureColumns}
          rows={featureRows}
          getRowKey={(row) => row.id}
          emptyContent={
            <p className="text-sm text-gray-500">No feature summary available.</p>
          }
        />
        <p className="mt-4 text-xs text-gray-500">
          Config is defined in backend accounts/entitlements.py. Update code to change entitlements.
        </p>
      </div>
    </div>
  );
};

export default EntitlementsManagement;
